import React, { useState, useEffect, useRef, useMemo } from "react";
import { SubtitleEditorModal } from "../SubtitleEditorModal";
import { AudioTrackOriginal } from "../AudioTrackOriginal";
import { DraggingTrackOriginal } from "../DraggingTrackOriginal";
import {
    AllFragmentsContainerSC,
    DeleteButtonWrapperSC,
    TopToolbarSC,
    TrackContainerSC,
    TrackSubtitlesContainerSC,
    TrackSubtitlesWrapperSC,
    VerticalTrackBackSC,
} from "./styled";
import { SubtitleBox } from "./components/SubtitleBox";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { usePreventPauseWithSpace } from "hooks/usePreventPauseWithSpace";
import { useFolderOffsetMs } from "hooks/useFolderOffsetMs";
import { formatTimeMilli } from "components/utils";
import { toGlobalMs } from "utils/globalTime";
import { DraggingTrackTranslated } from "../DraggingTrackTranslated";
import { RepublishSectionBadge } from "./RepublishSectionBadge";
import { useRepublishSection } from "providers/RepublishSectionProvider";
import { IAudioHandles } from "providers/AudioProvider/handleTypes";
import { AudioTrack, MetadataToSubtitlesLanguages, SelectedTranslatedTrack } from "providers/AudioProvider/types";
import { VoicingSilence } from "../VoicingSilence";
import { Button, InputNumber, Modal, Slider, message } from "antd";
import { useCorrection } from "../../../../../providers/CorrectionProvider";
import { createPortal } from "react-dom";
import { useSettingsSlot } from "utils/settingsSlots";
import { SETTINGS_ACTIONS } from "../Footer/settingsActions";
import { useVoicingSchedule } from "../../hooks/useVoicingSchedule";
import { useFollowPlayback } from "../../hooks/useFollowPlayback";
import { IVoiceType } from "types";
import { sourceLanguageNames, useUiLang } from "utils/uiLanguage";
import { captionConflicts } from "./captionCollisions";
import { CaptionConflictButton, CaptionConflictReview } from "./components/CaptionConflicts";
import { AutoEditGate, useAutoEditBlock } from "../ManualOnly/AutoEditGate";
import { useFocusRequestedCue } from "./useFocusRequestedCue";
import { useLinkTarget } from "./useLinkTarget";
import { cueHighlightCss } from "./linkTarget";
import { UnplayableVoicings } from "./components/UnplayableVoicings";
import { EditorErrorBanner } from "./components/EditorErrorBanner";
import { EditorNoticeBanner } from "./components/EditorNoticeBanner";
import { selectableVoicings, soundingVoicings } from "./utils";
import { newCuesRu } from "utils/pluralRu";

export const SubtitlesBlock = ({ height }: { height?: number }) => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const folderOffsetMs = useFolderOffsetMs();
    const { correctionsForMedia } = useCorrection();
    usePreventPauseWithSpace();
    // Shown in the settings panel, each in its own row. An action that does not apply here says
    // so, and the panel leaves its row out rather than showing an empty one.
    const republishSection = useRepublishSection();
    const uiLang = useUiLang();
    const en = uiLang === "en";
    // «Пометить готовым» names the source language, and a lecture translated FROM English has
    // no Russian in it to mark.
    const srcName = sourceLanguageNames(state.originalLang, uiLang);
    const recreateFromRuSlot = useSettingsSlot(SETTINGS_ACTIONS.recreateFromRu);
    // На эфире «Русский готов» и «Папка готова» не нужны: правки сами уходят в эфир (полоса правок эфира), а
    // сервер отвечает на них 409 с объяснением. Строки настроек на эфире не показываются.
    const isRealtimeStream = Boolean(state.realtimeEditorStatus?.is_realtime_stream);
    const markRuDoneSlot = useSettingsSlot(SETTINGS_ACTIONS.markRuDone, !isRealtimeStream);
    const handInSlot = useSettingsSlot(
        SETTINGS_ACTIONS.handIn,
        Boolean(!isRealtimeStream && state.toLang && state.lang !== state.originalLang),
    );
    // Must agree with the badge's own «нечего показывать» check (RepublishSectionBadge).
    const republishBadgeSlot = useSettingsSlot(
        SETTINGS_ACTIONS.republishBadge,
        Boolean(republishSection.answer?.now),
    );
    const trackRef = useRef<HTMLDivElement | null>(null);
    const translatedTracksRef = useRef(state.translationTrackAll);
    const scrollSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasRestoredScroll = useRef(false);
    const prevScrollKeyRef = useRef("");

    const [selectedTranslatedTrackIds, setSelectedTranslatedTrackIds] = useState<
        SelectedTranslatedTrack[]
    >([]);
    // Синхронизируем локальный стейт с провайдером для доступа из ResyncAudio (/fast/)
    useEffect(() => {
        handles.setSelectedTranslatedTrackIds(selectedTranslatedTrackIds);
    }, [selectedTranslatedTrackIds]);
    const [selectedOriginalTrackIds, setSelectedOriginalTrackIds] = useState<
        SelectedTranslatedTrack[]
    >([]);
    // Синхронизируем локальный стейт с провайдером для доступа из ResyncAudio (/fast/)
    useEffect(() => {
        handles.setSelectedOriginalTrackIds(selectedOriginalTrackIds);
    }, [selectedOriginalTrackIds]);

    const [positionDiff, setPositionDiff] = useState<number>(0);
    const [rerenderTracksIds, setRerenderTracksIds] = useState<string[]>([]);
    const [speedMode, setSpeedMode] = useState(false);
    const [bulkSpeed, setBulkSpeed] = useState<number>(1);
    const [bulkVoice, setBulkVoice] = useState<IVoiceType>("male");
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
    const [startHour, setStartHour] = useState<number | null>(null);
    const [startMin, setStartMin] = useState<number | null>(null);
    const [startSec, setStartSec] = useState<number | null>(null);
    const [endHour, setEndHour] = useState<number | null>(null);
    const [endMin, setEndMin] = useState<number | null>(null);
    const [endSec, setEndSec] = useState<number | null>(null);

    // Сдвиг
    const [shiftMode, setShiftMode] = useState(false);
    const [shiftStartHour, setShiftStartHour] = useState<number | null>(null);
    const [shiftStartMin, setShiftStartMin] = useState<number | null>(null);
    const [shiftStartSec, setShiftStartSec] = useState<number | null>(null);
    const [shiftEndHour, setShiftEndHour] = useState<number | null>(null);
    const [shiftEndMin, setShiftEndMin] = useState<number | null>(null);
    const [shiftEndSec, setShiftEndSec] = useState<number | null>(null);
    const [shiftValueHour, setShiftValueHour] = useState<number | null>(null);
    const [shiftValueMin, setShiftValueMin] = useState<number | null>(null);
    const [shiftValueSec, setShiftValueSec] = useState<number | null>(null);
    const [shiftValueMs, setShiftValueMs] = useState<number | null>(null);
    const [shiftDirection, setShiftDirection] = useState<"forward" | "backward">("forward");
    const [shiftLoading, setShiftLoading] = useState(false);

    const zoomLevel = 100 - state.zoomLevel;
    // Лента едет за воспроизведением сама: без этого звучащее место уходит вниз за край экрана,
    // и папку в пять минут переводчик прокручивает рукой десятки раз.
    const { pause: pauseFollow } = useFollowPlayback({
        container: trackRef,
        zoomLevel,
        playing: state.playing,
        enabled: state.followPlayback,
    });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
    const contextRef = useRef<Record<string, IAudioHandles>>({ handles });
    const onlyRuLang = state.lang === state.originalLang;
    // Новая реплика «+»: лента едет к ней, курсор — в её поле.
    useFocusRequestedCue({ zoomLevel, onlyRuLang });
    // Ссылка на реплику (`?cue=` из плеера эфира): лента едет к ней, реплика подсвечена лаймовой рамкой.
    const linkedCueRow = useLinkTarget({ container: trackRef, zoomLevel });
    const [reviewGroupKey, setReviewGroupKey] = useState<string | null>(null);
    const conflicts = useMemo(() => captionConflicts(
        state.translationTrackAll[state.originalLang] || [], zoomLevel,
    ), [state.translationTrackAll, state.originalLang, zoomLevel]);
    const conflictByIndex = useMemo(() => {
        const byIndex = new Map<number, typeof conflicts[number]>();
        conflicts.forEach((group) => group.entries.forEach(({ index }) => byIndex.set(index, group)));
        return byIndex;
    }, [conflicts]);
    useEffect(() => setReviewGroupKey(null), [state.mediaId, state.folder, state.lang, state.originalLang]);
    const openEditor = (oneTrack: AudioTrack) => {
        handles.showSubtitleEditor(oneTrack["id"]);
        handles.setPlaying(false);
        handles.setPlayingFragment(null);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const corrections = useMemo(() => {
        return correctionsForMedia.filter((item) => item.folder_num === state.folder.toString());
    }, [state.folder, correctionsForMedia]);

    useEffect(() => {
        translatedTracksRef.current = state.translationTrackAll;
    }, [state.translationTrackAll]);

    useEffect(() => {
        if (typeof state.scrollTo === "number" && trackRef.current) {
            trackRef.current.scrollTo({
                top: state.scrollTo,
                behavior: "smooth",
            });
            // Этот прыжок заказал человек — из отчёта проверок, из поиска по времени. Вернуть
            // ленту к звучащему месту в ту же секунду значит отменить его действие.
            pauseFollow();
            handles.setScrollTo(null);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [state.scrollTo, pauseFollow]);

    useEffect(() => {
        if (state.playingFragment && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [state.playingFragment]);

    useEffect(() => {
        if (!state.playing && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        } else {
            console.log(
                "Not clearing timeout1. Playing:",
                state.playing,
                "Timeout:",
                timeoutRef.current,
            );
        }
    }, [state.playing]);

    const currentPlayingFragmentRef = useRef<string | null>(null);

    useEffect(() => {
        currentPlayingFragmentRef.current = state.playingFragment;
    }, [state.playingFragment]);

    /**
     * Kept only because AudioPlayerTrack still takes the prop; it no longer schedules anything.
     *
     * It used to: a fragment reaching its end called this, which chained to the next one through
     * `setTimeout(gap)`. That is a prediction, and it was wrong whenever the original was not
     * exactly where the arithmetic assumed — after a click, after the drift trim, or simply
     * because the gap had passed while the tab was busy. Playback then stopped altogether,
     * because the promotion could only fire for a moment the original had already gone past.
     *
     * useVoicingSchedule answers the same question by looking instead of predicting.
     */
    const handlePlayNext = (): void => {};

    const handleCloseSubtitleEditor = () => {
        handles.showSubtitleEditor();
    };

    const handleDeleteSelected = () => {
        handles.deleteSubtitles(selectedOriginalTrackIds.map((item) => item.track.original_id));
        setSelectedOriginalTrackIds([]);
    };

    const translatedTracks = state.translationTrackAll[state.lang] || [];

    // Звучащие озвучки: снятая с озвучки (⊘) и озвучка без реплики оригинала не звучат и здесь —
    // их дубля нет в видео.
    const translatedTracksWithFile = useMemo(() => soundingVoicings(translatedTracks), [translatedTracks]);

    // Общий выбор (скорость, голос, сдвиг) не берёт озвучку без реплики оригинала — её только
    // удаляют (🗑 на блоке).
    const translatedTracksIndexed = useMemo(() => selectableVoicings(translatedTracks), [translatedTracks]);

    // The one thing that decides which voicing is sounding. Only cues that HAVE audio are
    // offered: selecting one without a file would leave nothing playing and look like a dropout.
    useVoicingSchedule({
        tracks: useMemo(
            () => translatedTracksWithFile.map(({ track }) => track),
            [translatedTracksWithFile],
        ),
    });


    const timeToMs = (hour: number | null, min: number | null, sec: number | null) =>
        ((hour || 0) * 3600 + (min || 0) * 60 + (sec || 0)) * 1000;

    const handleSelectByTime = (sHour: number | null, sMin: number | null, sSec: number | null, eHour: number | null, eMin: number | null, eSec: number | null) => {
        const hasStart = sHour != null || sMin != null || sSec != null;
        const hasEnd = eHour != null || eMin != null || eSec != null;
        if (!hasStart && !hasEnd) {
            setSelectedTranslatedTrackIds([]);
            return;
        }
        const startTotal = timeToMs(sHour, sMin, sSec);
        const endTotal = hasEnd ? timeToMs(eHour, eMin, eSec) : Infinity;
        if (endTotal <= startTotal) {
            setSelectedTranslatedTrackIds([]);
            return;
        }
        const selected = translatedTracksIndexed.filter(({ track }) => {
            const trackStart = track.start;
            const trackEnd = track.start + track.length;
            return trackStart < endTotal && trackEnd > startTotal;
        });
        setSelectedTranslatedTrackIds(selected);
    };

    const handleBulkApply = async () => {
        if (!selectedTranslatedTrackIds.length || bulkLoading) return;
        setBulkLoading(true);
        const total = selectedTranslatedTrackIds.length;
        setBulkProgress({ done: 0, total });
        const lang = MetadataToSubtitlesLanguages[state.lang];

        for (let i = 0; i < selectedTranslatedTrackIds.length; i++) {
            const { track } = selectedTranslatedTrackIds[i];
            try {
                await handles.audioSynthesize({
                    trackId: track.original_id,
                    folder: state.folder,
                    mediaId: state.mediaId,
                    lang,
                    voice: bulkVoice,
                    speed: bulkSpeed,
                    // Голос диапазону выбрал человек.
                    voiceNamed: true,
                });
            } catch (e) {
                console.error(`Bulk synth error for ${track.original_id}:`, e);
            }
            setBulkProgress({ done: i + 1, total });
        }

        // Обновляем все субтитры после массового изменения скорости,
        // чтобы правая колонка (перевод) корректно отобразила изменения
        await handles.refreshAllSubtitles(state.lang);

        setBulkLoading(false);
        setBulkProgress(null);
        setSelectedTranslatedTrackIds([]);
    };

    // Сдвиг: выбрать части по диапазону времени
    const shiftSelectedTracks = useMemo(() => {
        const hasStart = shiftStartHour != null || shiftStartMin != null || shiftStartSec != null;
        const hasEnd = shiftEndHour != null || shiftEndMin != null || shiftEndSec != null;
        if (!hasStart && !hasEnd) return translatedTracksIndexed; // все по умолчанию
        const startTotal = timeToMs(shiftStartHour, shiftStartMin, shiftStartSec);
        const endTotal = hasEnd ? timeToMs(shiftEndHour, shiftEndMin, shiftEndSec) : Infinity;
        if (endTotal <= startTotal) return [];
        return translatedTracksIndexed.filter(({ track }) => {
            const trackStart = track.start;
            const trackEnd = track.start + track.length;
            return trackStart < endTotal && trackEnd > startTotal;
        });
    }, [translatedTracksIndexed, shiftStartHour, shiftStartMin, shiftStartSec, shiftEndHour, shiftEndMin, shiftEndSec]);

    // Синхронизируем выбор из модалки сдвига с чекбоксами
    useEffect(() => {
        if (shiftMode) {
            setSelectedTranslatedTrackIds(shiftSelectedTracks);
        }
    }, [shiftSelectedTracks, shiftMode]);

    const handleShiftApply = async () => {
        if (!shiftSelectedTracks.length || shiftLoading) return;
        const shiftMs =
            ((shiftValueHour || 0) * 3600 + (shiftValueMin || 0) * 60 + (shiftValueSec || 0)) * 1000 +
            (shiftValueMs || 0);
        if (shiftMs === 0) {
            message.warning("Укажите величину сдвига");
            return;
        }
        const diffPosition = shiftDirection === "forward" ? shiftMs : -shiftMs;

        // Проверяем, не уйдёт ли какой-то трек в отрицательное время
        if (diffPosition < 0) {
            const minStart = Math.min(...shiftSelectedTracks.map(({ track }) => track.start));
            if (minStart + diffPosition < 0) {
                message.error(`Нельзя сдвинуть назад: первая часть начинается в ${Math.round(minStart)}мс, а сдвиг ${shiftMs}мс`);
                return;
            }
        }

        setShiftLoading(true);
        try {
            await handles.updateTranslatedTrackPositions({
                diffPosition,
                selectedList: shiftSelectedTracks.map(({ track }) => track),
            });
            message.success(`Сдвинуто ${shiftSelectedTracks.length} частей на ${shiftDirection === "forward" ? "+" : "−"}${shiftMs}мс`);
            setShiftMode(false);
            resetShiftState();
        } catch (e) {
            console.error("Shift error:", e);
            message.error("Ошибка сдвига");
        }
        setShiftLoading(false);
    };

    // «Пересоздать перевод по правкам русского» — explicit, owner-confirmed recreation:
    // dry-run first to show what will be touched, then the real run.
    // Заново переводит и переозвучивает реплики поверх работы переводчика, а ручка
    // /recreate_translated_from_ru флага пары не читает, — в режиме «Без автоправок» выключено.
    const recreateAutoEdit = useAutoEditBlock();
    const [recreateLoading, setRecreateLoading] = useState(false);
    const handleRecreateFromRu = async () => {
        if (recreateLoading || recreateAutoEdit.blocked) return;
        setRecreateLoading(true);
        try {
            const dry = await handles.recreateFromRu({ dry_run: true });
            if (dry.error || !dry.data) {
                message.error(dry.error || "Ошибка проверки правок");
                return;
            }
            const totals = dry.data.totals || { edited: 0, added: 0 };
            if (!totals.edited && !totals.added) {
                alert("Правок русского, требующих пересоздания, нет");
                return;
            }
            const ok = window.confirm(
                `Затронуто: правленых ${totals.edited} + ${newCuesRu(totals.added)}. Пересоздать перевод и озвучку?`,
            );
            if (!ok) return;
            const res = await handles.recreateFromRu({ dry_run: false });
            if (res.error || !res.data) {
                message.error(res.error || "Ошибка пересоздания");
                return;
            }
            const doneTotals = res.data.totals || totals;
            message.success(
                `Пересоздано: правленых ${doneTotals.edited} + ${newCuesRu(doneTotals.added)}`,
            );
            await handles.refreshAllSubtitles();
        } finally {
            setRecreateLoading(false);
        }
    };

    // «Русский готов» — the explicit per-folder mark that the Russian is finished
    // (owner's model): after it, translators take the folder over without the warning.
    const [ruDone, setRuDone] = useState(false);
    const [ruDoneLoading, setRuDoneLoading] = useState(false);
    useEffect(() => {
        let alive = true;
        if (isRealtimeStream) {
            setRuDone(false);
            return () => {
                alive = false;
            };
        }
        handles
            .getRuDone?.()
            .then((res: { data?: { folders?: Record<string, string> } }) => {
                if (alive && res?.data) {
                    setRuDone(Boolean(res.data.folders?.[String(state.folder)]));
                }
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [state.folder, state.mediaId, isRealtimeStream]);
    const handleToggleRuDone = async () => {
        if (ruDoneLoading) return;
        const next = !ruDone;
        if (!window.confirm(next
            ? `Пометить русский папки ${state.folder} завершённым?`
            : `Снять пометку «русский готов» с папки ${state.folder}?`)) return;
        setRuDoneLoading(true);
        try {
            const res = await handles.setRuDone({ folder_num: state.folder, done: next });
            if (res.error) {
                message.error(res.error);
            } else {
                setRuDone(next);
                message.success(next ? "Русский папки помечен готовым" : "Пометка снята");
            }
        } finally {
            setRuDoneLoading(false);
        }
    };

    // «Папка готова — в конвейер»: сдача папки переводчиком. Пометка ставится всегда, запуск
    // цепочки — отдельный факт: очередь может отказать (лекция занята), и переводчик должен
    // увидеть отказ дословно, а не зелёную галочку над невыполненным запуском.
    const [handedIn, setHandedIn] = useState(false);
    const [handInLoading, setHandInLoading] = useState(false);
    useEffect(() => {
        let alive = true;
        if (isRealtimeStream || !state.toLang || state.lang === state.originalLang) {
            setHandedIn(false);
            return () => {
                alive = false;
            };
        }
        handles
            .getTranslationDone?.()
            .then((res: { data?: { folders?: Record<string, string> } }) => {
                if (alive && res?.data) {
                    setHandedIn(Boolean(res.data.folders?.[String(state.folder)]));
                }
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [state.folder, state.mediaId, state.toLang, state.lang, state.originalLang, isRealtimeStream]);

    const handleHandIn = async () => {
        if (handInLoading) return;
        const next = !handedIn;
        if (!window.confirm(next
            ? `Сдать папку ${state.folder} (${state.toLang})?\n\n`
              + "Перевод и озвучка этой папки — уже ваша готовая работа, их никто не переделывает.\n"
              + "Дальше по лекции поедет:\n"
              + "• финальное видео (вся лекция)\n"
              + "• публикация\n\n"
              + (state.manualOnly
                  // Без автоправок сборка не переозвучивает и не переводит недостающее — она
                  // встанет с причиной. Сдающий должен знать это до сдачи, а не из отказа.
                  // Наложения она снимает и тут (правило владельца 01.10.2026) — только сдвигом
                  // позже; обещать «ровно как лежит» было бы неправдой. Размещение сборки ставит
                  // звук наехавшей озвучки ровно на конец предыдущей (+50 мс — у проходов по json).
                  ? "Режим «без автоправок»: сборка ничего не переозвучит и не переведёт за вас — "
                    + "в видео пойдёт то, что лежит в папке; сдвинется только озвучка, наехавшая на "
                    + "предыдущую, — позже, ровно на её конец. Реплика с текстом, но без озвучки, "
                    + "остановит сборку с причиной.\n\n"
                  : "")
              + "Если по лекции уже идёт задача, очередь откажет — сдача при этом останется."
            : `Снять пометку «папка сдана» с папки ${state.folder}? Ничего запущено не будет.`)) return;
        // Второй вопрос — только про проверку: озвучку вы слушали, пока делали, а могли и не
        // успеть. Проверка бесплатная (полнота озвучки + восстановимые wav) и, если не пройдена,
        // сборка не ставится — лекцию не пересоберут вокруг реплики без звука.
        const verify = next
            ? window.confirm(
                  `Проверить озвучку папки ${state.folder} перед сборкой?\n\n`
                      + "ОК — проверить (бесплатно; при провале сборка не поставится, папка всё равно сдана).\n"
                      + "Отмена — сразу собирать: вы уже прослушали озвучку.",
              )
            : false;
        setHandInLoading(true);
        try {
            const res = await handles.setTranslationDone({
                folder_num: state.folder,
                done: next,
                verify,
            });
            if (res.error) {
                message.error(res.error);
                return;
            }
            setHandedIn(next);
            const pipeline = res.data?.pipeline;
            const verification = res.data?.verification;
            if (!next) {
                message.success("Пометка снята");
            } else if (verification && verification.verdict !== "PASS" && !pipeline) {
                message.warning(
                    `Папка сдана, но сборка не поставлена: проверка озвучки — ${verification.verdict}`
                        + (verification.warnings?.length ? ` (${verification.warnings.join(", ")})` : "")
                        + (verification.message ? `. ${verification.message}` : ""),
                );
            } else if (pipeline?.queued) {
                message.success(
                    "Папка сдана"
                        + (verification?.verdict === "PASS" ? ", озвучка проверена" : "")
                        + ": поставлены финальное видео и публикация",
                );
            } else {
                message.warning(
                    `Папка сдана, но сборка не поставлена: ${pipeline?.message || "причина неизвестна"}`,
                );
            }
        } finally {
            setHandInLoading(false);
        }
    };

    const resetShiftState = () => {
        setShiftStartHour(null);
        setShiftStartMin(null);
        setShiftStartSec(null);
        setShiftEndHour(null);
        setShiftEndMin(null);
        setShiftEndSec(null);
        setShiftValueHour(null);
        setShiftValueMin(null);
        setShiftValueSec(null);
        setShiftValueMs(null);
        setShiftDirection("forward");
    };

    const scrollStorageKey = `scrollPos_${state.mediaId}_${state.folder}`;

    // Save scroll position on scroll (debounced)
    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        const handleScroll = () => {
            if (scrollSaveTimerRef.current) {
                clearTimeout(scrollSaveTimerRef.current);
            }
            scrollSaveTimerRef.current = setTimeout(() => {
                sessionStorage.setItem(scrollStorageKey, String(el.scrollTop));
            }, 200);
        };
        el.addEventListener("scroll", handleScroll);
        return () => {
            el.removeEventListener("scroll", handleScroll);
            if (scrollSaveTimerRef.current) {
                clearTimeout(scrollSaveTimerRef.current);
            }
        };
    }, [scrollStorageKey]);

    // Restore scroll position once content is loaded and container has height
    useEffect(() => {
        // Reset restore flag when navigating to a different media/folder
        if (prevScrollKeyRef.current !== scrollStorageKey) {
            hasRestoredScroll.current = false;
            prevScrollKeyRef.current = scrollStorageKey;
        }
        if (
            hasRestoredScroll.current ||
            !trackRef.current ||
            !state.translationTrackAll[state.originalLang]?.length ||
            !state.durationFull
        ) {
            return;
        }
        hasRestoredScroll.current = true;
        const saved = sessionStorage.getItem(scrollStorageKey);
        if (saved) {
            const scrollTop = Number(saved);
            // Use double requestAnimationFrame to ensure DOM layout is fully complete
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (trackRef.current) {
                        trackRef.current.scrollTop = scrollTop;
                    }
                });
            });
        }
    }, [state.translationTrackAll, scrollStorageKey, state.originalLang, state.durationFull]);

    return (
        <>
            {linkedCueRow && <style data-cue-target={linkedCueRow}>{cueHighlightCss(linkedCueRow)}</style>}
            {state.editingSubtitles && (
                <SubtitleEditorModal
                    oneTrackId={state.editingSubtitles}
                    handleClose={handleCloseSubtitleEditor}
                />
            )}
            {selectedOriginalTrackIds.length > 0 && (
                <DeleteButtonWrapperSC>
                    <Button type={"primary"} onClick={handleDeleteSelected}>
                        Delete
                    </Button>
                </DeleteButtonWrapperSC>
            )}

            <Modal
                title={`Скорость — выбрано: ${selectedTranslatedTrackIds.length}`}
                open={speedMode}
                onCancel={() => {
                    setSpeedMode(false);
                    setSelectedTranslatedTrackIds([]);
                    setStartHour(null);
                    setStartMin(null);
                    setStartSec(null);
                    setEndHour(null);
                    setEndMin(null);
                    setEndSec(null);
                }}
                footer={null}
                width={520}
                styles={{ body: { padding: "12px 0" } }}
            >
                <div style={{ display: "flex", gap: 8, marginBottom: 12, padding: "0 12px" }}>
                    <Button
                        size="small"
                        onClick={() => setSelectedTranslatedTrackIds(translatedTracksIndexed)}
                    >
                        Выбрать все ({translatedTracksIndexed.length})
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setSelectedTranslatedTrackIds(translatedTracksWithFile)}
                    >
                        Все с озвучкой ({translatedTracksWithFile.length})
                    </Button>
                    {selectedTranslatedTrackIds.length > 0 && (
                        <Button
                            size="small"
                            onClick={() => setSelectedTranslatedTrackIds([])}
                        >
                            Сбросить
                        </Button>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12, padding: "0 12px" }}>
                    {/* Ввод остался в локальных числах — так работает выборка по track.start. Раз
                        рядом теперь видно и время от начала видео, надо сказать это прямо. */}
                    <div style={{ fontWeight: 500 }}>Выбор по времени — от начала папки:</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontWeight: 500, width: 40 }}>Start:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            value={startHour}
                            placeholder="час"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setStartHour(val);
                                handleSelectByTime(val, startMin, startSec, endHour, endMin, endSec);
                            }}
                        />
                        <span>:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={59}
                            value={startMin}
                            placeholder="мин"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setStartMin(val);
                                handleSelectByTime(startHour, val, startSec, endHour, endMin, endSec);
                            }}
                        />
                        <span>:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={59}
                            value={startSec}
                            placeholder="сек"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setStartSec(val);
                                handleSelectByTime(startHour, startMin, val, endHour, endMin, endSec);
                            }}
                        />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontWeight: 500, width: 40 }}>End:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            value={endHour}
                            placeholder="час"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setEndHour(val);
                                handleSelectByTime(startHour, startMin, startSec, val, endMin, endSec);
                            }}
                        />
                        <span>:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={59}
                            value={endMin}
                            placeholder="мин"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setEndMin(val);
                                handleSelectByTime(startHour, startMin, startSec, endHour, val, endSec);
                            }}
                        />
                        <span>:</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={59}
                            value={endSec}
                            placeholder="сек"
                            style={{ width: 65 }}
                            onChange={(val) => {
                                setEndSec(val);
                                handleSelectByTime(startHour, startMin, startSec, endHour, endMin, val);
                            }}
                        />
                    </div>
                </div>

                {selectedTranslatedTrackIds.length > 0 && (
                    <div style={{
                        maxHeight: 250,
                        overflowY: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 6,
                        margin: "0 12px",
                        padding: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                    }}>
                        {selectedTranslatedTrackIds.map(({ ind, track }) => {
                            const trackStartFmt = formatTimeMilli(track.start);
                            const trackEndFmt = formatTimeMilli(track.start + track.length);
                            // Рядом — то же самое от начала видео: оператор вводит диапазон в
                            // локальных числах, а называет коллеге глобальные. Без этой пары
                            // связь между ними приходится держать в голове.
                            const globalStart = toGlobalMs(track.start, folderOffsetMs);
                            const globalEnd = toGlobalMs(track.start + track.length, folderOffsetMs);
                            return (
                                <div
                                    key={track.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "4px 8px",
                                        backgroundColor: "#e6f4ff",
                                        border: "1px solid #1677ff",
                                        borderRadius: 4,
                                        fontSize: 13,
                                    }}
                                >
                                    <span>#{ind + 1}</span>
                                    <span style={{ color: "#666" }}>
                                        {trackStartFmt} — {trackEndFmt}
                                        {globalStart !== null && globalEnd !== null ? (
                                            <span style={{ marginLeft: 6, color: "#8c8c8c" }}>
                                                · видео {formatTimeMilli(globalStart)} —{" "}
                                                {formatTimeMilli(globalEnd)}
                                            </span>
                                        ) : null}
                                    </span>
                                    {track.speed != null && (
                                        <span style={{ fontSize: 11, color: "#999" }}>
                                            {track.speed}x
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ padding: "12px 12px 0", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span>Голос:</span>
                        <select
                            style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: "1px solid #d9d9d9" }}
                            value={bulkVoice}
                            onChange={(e) => setBulkVoice(e.target.value as IVoiceType)}
                        >
                            <option value="male">Oleg Torsunov</option>
                            <option value="male1">Audience: Male</option>
                            <option value="female">Audience: Female</option>
                            <option value="none">Without translation</option>
                        </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span>Скорость:</span>
                        <Slider
                            style={{ flex: 1 }}
                            min={70}
                            max={120}
                            step={1}
                            value={bulkSpeed * 100}
                            onChange={(val: number) => setBulkSpeed(Math.floor(val / 10) / 10)}
                        />
                        <b>{bulkSpeed}</b>
                    </div>
                    <Button
                        type="primary"
                        block
                        loading={bulkLoading}
                        disabled={selectedTranslatedTrackIds.length === 0}
                        onClick={handleBulkApply}
                    >
                        {bulkProgress
                            ? `${bulkProgress.done} / ${bulkProgress.total}`
                            : `Применить (${selectedTranslatedTrackIds.length})`}
                    </Button>
                </div>
            </Modal>

            <Modal
                title={`Сдвиг — затронуто: ${shiftSelectedTracks.length}`}
                open={shiftMode}
                onCancel={() => {
                    // Выбор остаётся при закрытии — можно перетаскивать
                    setShiftMode(false);
                    resetShiftState();
                }}
                footer={null}
                width={520}
                styles={{ body: { padding: "12px 0" } }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 12px" }}>
                    <div>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            Диапазон — от начала папки (пусто = все):
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                            <span style={{ width: 30, fontSize: 12 }}>От:</span>
                            <InputNumber size="small" min={0} value={shiftStartHour} placeholder="ч" style={{ width: 58 }} onChange={setShiftStartHour} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftStartMin} placeholder="м" style={{ width: 58 }} onChange={setShiftStartMin} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftStartSec} placeholder="с" style={{ width: 58 }} onChange={setShiftStartSec} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ width: 30, fontSize: 12 }}>До:</span>
                            <InputNumber size="small" min={0} value={shiftEndHour} placeholder="ч" style={{ width: 58 }} onChange={setShiftEndHour} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftEndMin} placeholder="м" style={{ width: 58 }} onChange={setShiftEndMin} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftEndSec} placeholder="с" style={{ width: 58 }} onChange={setShiftEndSec} />
                        </div>
                    </div>

                    <div>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>Сдвинуть на:</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <select
                                style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #d9d9d9" }}
                                value={shiftDirection}
                                onChange={(e) => setShiftDirection(e.target.value as "forward" | "backward")}
                            >
                                <option value="forward">+ вперёд</option>
                                <option value="backward">− назад</option>
                            </select>
                            <InputNumber size="small" min={0} value={shiftValueHour} placeholder="ч" style={{ width: 58 }} onChange={setShiftValueHour} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftValueMin} placeholder="м" style={{ width: 58 }} onChange={setShiftValueMin} />
                            <span>:</span>
                            <InputNumber size="small" min={0} max={59} value={shiftValueSec} placeholder="с" style={{ width: 58 }} onChange={setShiftValueSec} />
                            <span>.</span>
                            <InputNumber size="small" min={0} max={999} value={shiftValueMs} placeholder="мс" style={{ width: 65 }} onChange={setShiftValueMs} />
                        </div>
                    </div>

                    {shiftSelectedTracks.length > 0 && (
                        <div style={{
                            maxHeight: 200,
                            overflowY: "auto",
                            border: "1px solid #f0f0f0",
                            borderRadius: 6,
                            padding: 8,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                        }}>
                            {shiftSelectedTracks.map(({ ind, track }) => {
                                return (
                                    <div
                                        key={track.id}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "3px 8px",
                                            backgroundColor: "#fff7e6",
                                            border: "1px solid #fa8c16",
                                            borderRadius: 4,
                                            fontSize: 12,
                                        }}
                                    >
                                        <span>#{ind + 1}</span>
                                        <span style={{ color: "#666" }}>
                                            {formatTimeMilli(track.start)} —{" "}
                                            {formatTimeMilli(track.start + track.length)}
                                            {toGlobalMs(track.start, folderOffsetMs) !== null ? (
                                                <span style={{ marginLeft: 6, color: "#8c8c8c" }}>
                                                    · видео{" "}
                                                    {formatTimeMilli(
                                                        toGlobalMs(track.start, folderOffsetMs) as number,
                                                    )}{" "}
                                                    —{" "}
                                                    {formatTimeMilli(
                                                        toGlobalMs(
                                                            track.start + track.length,
                                                            folderOffsetMs,
                                                        ) as number,
                                                    )}
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ display: "flex", gap: 8 }}>
                        <Button
                            block
                            disabled={shiftSelectedTracks.length === 0}
                            onClick={() => {
                                // Закрываем модалку, но выбор остаётся — можно перетаскивать мышкой
                                setShiftMode(false);
                                resetShiftState();
                            }}
                        >
                            Выбрать и закрыть ({shiftSelectedTracks.length})
                        </Button>
                        <Button
                            type="primary"
                            block
                            loading={shiftLoading}
                            disabled={shiftSelectedTracks.length === 0}
                            onClick={handleShiftApply}
                        >
                            Сдвинуть ({shiftSelectedTracks.length})
                        </Button>
                    </div>
                </div>
            </Modal>

            <TrackSubtitlesWrapperSC height={height}>
                <EditorErrorBanner
                    message={state.translatedOpError}
                    refusedEdits={state.refusedEdits || 0}
                    onClose={handles.clearTranslatedOpError}
                    onDiscard={() => void handles.discardRefusedEdits()}
                />
                <EditorNoticeBanner
                    message={state.overlapPushNotice}
                    onClose={handles.clearOverlapPushNotice}
                />
                {!onlyRuLang && <UnplayableVoicings tracks={translatedTracks} />}
                <TopToolbarSC>
                    <div
                        className="toolbar-btn"
                        onClick={() => setSpeedMode(true)}
                    >
                        ⚡ Скорость
                    </div>
                    <div
                        className="toolbar-btn"
                        onClick={() => setShiftMode(true)}
                    >
                        ↔ Сдвиг
                    </div>
                </TopToolbarSC>
                {/* These four are shown in the settings panel, each in its own row: the panel
                    supplies the name and the explanation, so the labels here are short. */}
                {recreateFromRuSlot &&
                    createPortal(
                        <AutoEditGate {...recreateAutoEdit}>
                            <div
                                className="toolbar-btn"
                                style={recreateLoading ? { opacity: 0.5, cursor: "wait" } : undefined}
                                onClick={handleRecreateFromRu}
                            >
                                {en ? "♻ Recreate" : "♻ Пересоздать"}
                            </div>
                        </AutoEditGate>,
                        recreateFromRuSlot,
                    )}
                {markRuDoneSlot && !isRealtimeStream &&
                    createPortal(
                        <div
                            className="toolbar-btn"
                            style={ruDoneLoading ? { opacity: 0.5, cursor: "wait" } : undefined}
                            onClick={handleToggleRuDone}
                        >
                            {ruDone
                                ? `✓ ${srcName.nom} ${en ? "is done" : "готов"}`
                                : en
                                  ? "Mark as finished"
                                  : "Пометить готовым"}
                        </div>,
                        markRuDoneSlot,
                    )}
                {/* Сдача папки переводчиком: пометка + запуск остатка конвейера до
                    переопубликации. Кнопка есть только на стороне перевода — на мастере
                    сдавать нечего, и её строка в настройках прячется сама. */}
                {handInSlot && !isRealtimeStream && state.toLang && state.lang !== state.originalLang
                    ? createPortal(
                          <div
                              className="toolbar-btn"
                              style={handInLoading ? { opacity: 0.5, cursor: "wait" } : undefined}
                              onClick={handleHandIn}
                          >
                              {handedIn
                                  ? en
                                      ? "✓ Folder handed in"
                                      : "✓ Папка сдана"
                                  : en
                                    ? "📤 Hand on to the pipeline"
                                    : "📤 Отправить в конвейер"}
                          </div>,
                          handInSlot,
                      )
                    : null}
                {republishBadgeSlot && createPortal(<RepublishSectionBadge />, republishBadgeSlot)}
                <CaptionConflictReview group={conflicts.find((group) => group.key === reviewGroupKey) || null}
                    onClose={() => setReviewGroupKey(null)} openEditor={openEditor} />
                <TrackSubtitlesContainerSC ref={trackRef}>
                    <AudioTrackOriginal />

                    {Boolean(state.translationTrackAll[state.originalLang]?.length) && (
                        <AllFragmentsContainerSC
                            height={
                                state["durationFull"]
                                    ? `${state["durationFull"] / zoomLevel}px`
                                    : "auto"
                            }
                            left="true"
                        >
                            <VerticalTrackBackSC left="true" />

                            {state.translationTrackAll[state.originalLang]?.map(
                                (oneTrack: AudioTrack, ind: number) => {
                                    const conflict = conflictByIndex.get(ind);
                                    return (
                                        <TrackContainerSC key={`track_${oneTrack["id"]}_${ind}`}>
                                            <DraggingTrackOriginal
                                                key={`track_${oneTrack["id"]}_${ind}`}
                                                oneTrack={oneTrack}
                                                ind={ind}
                                                id={oneTrack["id"]}
                                                start={oneTrack["start"]}
                                                trimmed_start={oneTrack["trimmed_start"] || 0}
                                                length={oneTrack.length}
                                                trimmed={oneTrack.trimmed}
                                                tracks={
                                                    state.translationTrackAll[state.originalLang] ||
                                                    []
                                                }
                                                timeoutRef={timeoutRef}
                                                draggingTrack={trackRef}
                                                selectedOriginalTrackIds={selectedOriginalTrackIds}
                                                setSelectedOriginalTrackIds={
                                                    setSelectedOriginalTrackIds
                                                }
                                            >
                                                {state.video !== "full" && conflict && conflict.entries[0].index === ind && (
                                                    <CaptionConflictButton group={conflict} onClick={() => setReviewGroupKey(conflict.key)} />
                                                )}
                                                {state.video !== "full" && !conflict && (
                                                    <>
                                                        <SubtitleBox
                                                            oneTrack={oneTrack}
                                                            zoomLevel={zoomLevel}
                                                            openEditor={openEditor}
                                                            onTranslate={handles.translateSubtitle}
                                                            isFullWidth={onlyRuLang}
                                                        />
                                                        {!onlyRuLang && (
                                                            <SubtitleBox
                                                                oneTrack={oneTrack}
                                                                zoomLevel={zoomLevel}
                                                                openEditor={openEditor}
                                                                isRightSide
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </DraggingTrackOriginal>
                                        </TrackContainerSC>
                                    );
                                },
                            )}
                        </AllFragmentsContainerSC>
                    )}

                    {/* add here second column with track all fragments */}

                    {!onlyRuLang && Boolean(state.translationTrackAll[state.lang]?.length) && (
                        <AllFragmentsContainerSC
                            height={
                                state["durationFull"]
                                    ? `${state["durationFull"] / zoomLevel}px`
                                    : "auto"
                            }
                            right="true"
                        >
                            <VerticalTrackBackSC right="true" />
                            <VoicingSilence />

                            {state.translationTrackAll[state.lang]?.map(
                                (oneTrack: AudioTrack, ind: number) => {
                                    return (
                                        <TrackContainerSC
                                            style={{ right: 0 }}
                                            key={`track_${oneTrack["id"]}_${ind}`}
                                        >
                                            <DraggingTrackTranslated
                                                key={`track_${oneTrack["id"]}_${ind}`}
                                                oneTrack={oneTrack}
                                                ind={ind}
                                                id={oneTrack["id"]}
                                                start={oneTrack["start"]}
                                                trimmed_start={oneTrack["trimmed_start"] || 0}
                                                length={oneTrack.length}
                                                trimmed={oneTrack.trimmed}
                                                tracks={state.translationTrackAll[state.lang] || []}
                                                timeoutRef={timeoutRef}
                                                draggingTrack={trackRef}
                                                handlePlayNext={handlePlayNext}
                                                selectedTranslatedTrackIds={
                                                    selectedTranslatedTrackIds
                                                }
                                                setSelectedTranslatedTrackIds={
                                                    setSelectedTranslatedTrackIds
                                                }
                                                positionDiff={positionDiff}
                                                setPositionDiff={setPositionDiff}
                                                rerenderTracksIds={rerenderTracksIds}
                                                setRerenderTracksIds={setRerenderTracksIds}
                                            />
                                        </TrackContainerSC>
                                    );
                                },
                            )}
                        </AllFragmentsContainerSC>
                    )}

                    {state.subtitlesRequest.status === "success" &&
                        !state.subtitlesDataAll[state.originalLang]?.[state.folder - 1]?.translations.length && (
                        <>
                            <div
                                style={{
                                    display: "flex",
                                    rowGap: "1em",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    padding: "10vh",
                                }}
                            >
                                <div className="ico ico--100">subtitles_off</div>
                                There are no subtitles yet
                            </div>
                        </>
                    )}
                </TrackSubtitlesContainerSC>
            </TrackSubtitlesWrapperSC>
        </>
    );
};
